/**
 * Parking — vagas ao redor de cada edifício, em ordem de PREFERÊNCIA de
 * visibilidade (frente → laterais → fundos):
 *
 *   • CARROS estacionam JUNTO AO MEIO-FIO, na RUA que cerca a quadra (nunca na
 *     calçada). Preferência: rua da frente (lado da porta) → ruas laterais →
 *     rua dos fundos.
 *   • PESSOAS ficam na CALÇADA da quadra (anel pavimentado), encarando o prédio.
 *
 * Cada vaga tem heading. As de carro ficam paralelas ao meio-fio; as de pessoa
 * apontam para o edifício.
 */
import { ROADS, WORLD, isDrivable } from '../config.js';
import {
  BUILDINGS, SIDEWALK, PARK, BENCHES, generateTrees, makeRandom,
  DISTRICT_PATH, districtWalk, districtPavement, districtWalkGate
} from './cityLayout.js';

const ROAD_HALF = ROADS.width / 2;
// O carro para NA RUA, colado no meio-fio (nunca sobe na calçada): o CENTRO do carro
// fica CAR_CURB para DENTRO da rua a partir do meio-fio. Meia-largura RENDERIZADA ≈ 1.3
// (para-choque 2.06 × CAR_SCALE 1.25) → com 1.5 a lateral fica ~0.2 na rua (colado, sem
// invadir). O carro chega PARALELO (Fleet/CityAgents), então só a largura encara o
// meio-fio — o nariz (~3.2 de comprimento) fica ao longo da via, nunca apontado à calçada.
export const CAR_CURB = 1.5;
const WALK = SIDEWALK / 2; // centro da calçada (vaga de pessoa)
const CAR_STEP = 6.5;   // espaço por carro (comprido)
const PED_STEP = 4.2;   // espaço por pedestre parado (mais afastados, legível)

// ordena posições do CENTRO para FORA → as vagas se preenchem na frente do
// prédio primeiro e só espalham para as esquinas quando enchem (evita carros/
// pessoas amontoados nas pontas das quadras).
function centerOut(vals) {
  const mid = (vals.length - 1) / 2;
  return vals
    .map((v, i) => ({ v, d: Math.abs(i - mid) }))
    .sort((a, b) => a.d - b.d)
    .map((o) => o.v);
}

/* --- existência de rua (evita "estacionar" na floresta nas bordas) --- */
// `isDrivable`: o eixo interno de uma superquadra virou calçada — não tem meio-fio de
// verdade ali, então não pode virar vaga (o carro estacionaria dentro do parque).
function roadAtZ(z, xMin, xMax) {
  return ROADS.horizontal.some(
    (r) => isDrivable(r) && Math.abs(r.z - z) < 1 && r.xMin <= xMin + 1 && r.xMax >= xMax - 1
  );
}
function roadAtX(x, zMin, zMax) {
  return ROADS.vertical.some(
    (r) => isDrivable(r) && Math.abs(r.x - x) < 1 && r.zMin <= zMin + 1 && r.zMax >= zMax - 1
  );
}

/** Vagas de CARRO (NA RUA, coladas no meio-fio da quadra), em ordem de preferência.
 *  O carro para aqui (na rua) e vira pessoa girando no meio-fio; a pessoa então ANDA
 *  para dentro da calçada. Frente/fundos: rua no ±Z além de bottom/top; laterais: rua
 *  no ±X além de left/right. A calçada é sempre o lado de DENTRO da quadra. */
function carSlotsFor(b) {
  const slots = [];
  const hx = b.w / 2, hz = b.d / 2;
  const { left, right, top, bottom } = b.land;
  // carZ/carX = posição do CARRO (na rua, colado no meio-fio); heading paralelo ao meio-fio
  const rowX = (carZ, head) => {
    const span = hx + 2;
    const n = Math.max(1, Math.round((span * 2) / CAR_STEP));
    const xs = [];
    for (let i = 0; i <= n; i++) xs.push(b.x - span + (span * 2 * i) / n);
    for (const x of centerOut(xs)) slots.push({ x, z: carZ, heading: head });
  };
  const rowZ = (carX, head) => {
    const span = hz + 2;
    const n = Math.max(1, Math.round((span * 2) / CAR_STEP));
    const zs = [];
    for (let i = 0; i <= n; i++) zs.push(b.z - span + (span * 2 * i) / n);
    for (const z of centerOut(zs)) slots.push({ x: carX, z, heading: head });
  };
  // frente (+Z): rua além de bottom (carro em bottom+CAR_CURB, na rua)
  if (roadAtZ(bottom + ROAD_HALF, b.x - hx, b.x + hx)) rowX(bottom + CAR_CURB, Math.PI / 2);
  // laterais (±X): rua além de left (-X) / right (+X)
  if (roadAtX(left - ROAD_HALF, b.z - hz, b.z + hz)) rowZ(left - CAR_CURB, 0);
  if (roadAtX(right + ROAD_HALF, b.z - hz, b.z + hz)) rowZ(right + CAR_CURB, 0);
  // fundos (-Z): rua além de top (carro em top-CAR_CURB, na rua)
  if (roadAtZ(top - ROAD_HALF, b.x - hx, b.x + hx)) rowX(top - CAR_CURB, Math.PI / 2);
  return slots;
}

/**
 * As 4 linhas de calçada que cercam o prédio. Quadra comum: o meio do anel da própria
 * quadra. SUPERQUADRA: o anel da quadra não existe mais — de cada lado do CANTEIRO o
 * piso é o anel do perímetro do distrito (quando o canteiro encosta na borda do bloco)
 * ou a passarela daquele vão (quando ele dá para um vizinho). É a mesma regra que centra
 * o prédio no canteiro (cityLayout.districtBed), lida do lado de fora.
 *
 * Na passarela a linha é a MEIA-PISTA do lado do prédio, não o eixo do vão: a passarela é
 * COMPARTILHADA com o vizinho de frente, e no eixo as duas filas cairiam na mesma
 * coordenada — dois prédios posicionando gente exatamente uma dentro da outra.
 *
 * Os lados saem das BORDAS DO CANTEIRO (que são as próprias passarelas — ver
 * DISTRICT_WALK.xLines/zLines), não dos índices de coluna/linha do lote: com os
 * canteiros empacotados o eixo da rua que sumiu não é mais o vão, e um lote MESCLADO
 * (o shopping, 2 colunas) não tem uma borda por coluna.
 */
function walkLinesFor(b) {
  const { left, right, top, bottom } = b.land;
  const W = b.bed && districtWalk(b.bed.district);
  if (!W) return { left: left + WALK, right: right - WALK, top: top + WALK, bottom: bottom - WALK };
  const bed = b.bed, s = DISTRICT_PATH / 4; // meia-pista da passarela (vão de 6 → 1,5)
  const lane = (lines, v) => lines.find((l) => Math.abs(l - v) < 0.5); // vão nesta borda?
  const xL = lane(W.xLines, bed.left), xR = lane(W.xLines, bed.right);
  const zT = lane(W.zLines, bed.top), zB = lane(W.zLines, bed.bottom);
  return {
    left: xL != null ? xL + s : W.x0,
    right: xR != null ? xR - s : W.x1,
    top: zT != null ? zT + s : W.z0,
    bottom: zB != null ? zB - s : W.z1
  };
}

/** Vagas de PESSOA (na calçada da quadra), em ordem de preferência. */
function pedSlotsFor(b) {
  const slots = [];
  const hx = b.w / 2, hz = b.d / 2;
  const { left, right, top, bottom } = walkLinesFor(b);
  const add = (x, z) => slots.push({ x, z, heading: Math.atan2(b.x - x, b.z - z) });
  const rowX = (z) => {
    const span = hx + 2;
    const n = Math.max(1, Math.round((span * 2) / PED_STEP));
    const xs = [];
    for (let i = 0; i <= n; i++) xs.push(b.x - span + (span * 2 * i) / n);
    for (const x of centerOut(xs)) add(x, z);
  };
  const rowZ = (x) => {
    const span = hz + 2;
    const n = Math.max(1, Math.round((span * 2) / PED_STEP));
    const zs = [];
    for (let i = 0; i <= n; i++) zs.push(b.z - span + (span * 2 * i) / n);
    for (const z of centerOut(zs)) add(x, z);
  };
  rowX(bottom);  // frente (+Z)
  rowZ(left);    // laterais
  rowZ(right);
  rowX(top);     // fundos
  // Na superquadra as linhas são longas e passam pelo largo: descarta o que caiu no
  // jardim da fonte (as únicas posições da camada 0 que não são piso).
  return b.bed ? slots.filter((s) => districtPavement(s.x, s.z)) : slots;
}

const CAR_SLOTS = BUILDINGS.map(carSlotsFor);
const CAR_OCC = BUILDINGS.map(() => new Set());

/* ================== PORTAIS DE CHEGADA (modo sem colisão carro-carro) ==================
 *
 * Cada edifício é representado por até QUATRO portais — frente, fundos e laterais —
 * criados SÓ nas faces que realmente têm rua (as MESMAS condições roadAtZ/roadAtX das
 * vagas, então portal e vaga nunca divergem sobre onde existe rua; superquadras e
 * layouts especiais ganham só as faces que de fato têm via dirigível).
 *
 * Um portal NÃO é um ponto: é um SEGMENTO de meio-fio [lo, hi] com margem das
 * esquinas/cruzamentos. No modo sem colisão entre carros o agente escolhe o portal de
 * MENOR CUSTO REAL de rota (CityAgents._choosePortal), recebe um ponto NOMINAL
 * determinístico dentro do segmento (hash de jogador+prédio+face+geração — espalha os
 * carros por ~75% da extensão útil) e pode transformar em QUALQUER ponto da zona do
 * segmento — sem reservar vaga, sem CAR_OCC, sem dar volta no quarteirão atrás de uma
 * vaga exata. Com colisão ligada nada disto é usado (o fluxo de reservas continua).
 */
const PORTAL_CORNER_MARGIN = 6; // u de folga das esquinas (não transforma no cruzamento)
export const PORTAL_SPAN = 0.75; // fração central do segmento usada pelos pontos nominais

function portalsFor(b) {
  const portals = [];
  const hx = b.w / 2, hz = b.d / 2;
  const { left, right, top, bottom } = b.land;
  const c = { x: b.x, z: b.z };
  const mk = (face, axis, fixed, min, max, heading) => {
    const lo = min + PORTAL_CORNER_MARGIN, hi = max - PORTAL_CORNER_MARGIN;
    if (hi - lo < 4) return; // face curta demais p/ um segmento útil
    // normal p/ a RUA e sentido da faixa PRÓXIMA (mão americana) — a MESMA derivação
    // do _parkWaypoint do CityAgents, calculada uma vez por portal
    let nx = Math.cos(heading), nz = -Math.sin(heading);
    const px = axis === 'x' ? (lo + hi) / 2 : fixed;
    const pz = axis === 'x' ? fixed : (lo + hi) / 2;
    if ((px - c.x) * nx + (pz - c.z) * nz < 0) { nx = -nx; nz = -nz; }
    portals.push({ face, axis, fixed, lo, hi, heading, nx, nz, fx: -nz, fz: nx });
  };
  // as MESMAS quatro condições do carSlotsFor (rua de verdade ao longo da face)
  if (roadAtZ(bottom + ROAD_HALF, b.x - hx, b.x + hx)) mk('frente', 'x', bottom + CAR_CURB, b.x - (hx + 2), b.x + (hx + 2), Math.PI / 2);
  if (roadAtX(left - ROAD_HALF, b.z - hz, b.z + hz)) mk('esq', 'z', left - CAR_CURB, b.z - (hz + 2), b.z + (hz + 2), 0);
  if (roadAtX(right + ROAD_HALF, b.z - hz, b.z + hz)) mk('dir', 'z', right + CAR_CURB, b.z - (hz + 2), b.z + (hz + 2), 0);
  if (roadAtZ(top - ROAD_HALF, b.x - hx, b.x + hx)) mk('fundos', 'x', top - CAR_CURB, b.x - (hx + 2), b.x + (hx + 2), Math.PI / 2);
  return portals;
}
const PORTALS = BUILDINGS.map(portalsFor);

/** Portais de chegada do edifício `i` (até 4; só faces com rua). */
export function carPortals(i) {
  return PORTALS[i] || [];
}

/** Ponto no MEIO-FIO do portal na fração `t` ∈ [0,1] do segmento útil. */
export function portalPoint(p, t) {
  const along = p.lo + (p.hi - p.lo) * t;
  return p.axis === 'x' ? { x: along, z: p.fixed } : { x: p.fixed, z: along };
}

export const BUILDING_COUNT = BUILDINGS.length;

export function buildingPoint(i) {
  const b = BUILDINGS[i];
  return { x: b.x, z: b.z };
}

function acquire(list, occ, i) {
  const o = occ[i], L = list[i];
  for (let s = 0; s < L.length; s++) {
    if (!o.has(s)) {
      o.add(s);
      return { index: s, x: L[s].x, z: L[s].z, heading: L[s].heading };
    }
  }
  return null;
}

export function acquireCarSlot(i) {
  return acquire(CAR_SLOTS, CAR_OCC, i);
}
/** Vaga de CARRO LIVRE mais próxima de (x,z) — o carro encosta no meio-fio mais perto de
 *  onde está, em vez de dar a volta na quadra até a "vaga preferencial" da frente. Espelha
 *  acquirePersonSlotNear. Empate resolve pela ordem de PREFERÊNCIA (frente → laterais →
 *  fundos), então com pouca gente o resultado continua sendo a frente do prédio. */
export function acquireCarSlotNear(i, x, z) {
  const o = CAR_OCC[i], L = CAR_SLOTS[i];
  let best = -1, bd = Infinity;
  for (let s = 0; s < L.length; s++) {
    if (o.has(s)) continue;
    const d = (L[s].x - x) ** 2 + (L[s].z - z) ** 2;
    if (d < bd) { bd = d; best = s; }
  }
  if (best === -1) return null;
  o.add(best);
  return { index: best, x: L[best].x, z: L[best].z, heading: L[best].heading };
}
/** Posição de MEIO-FIO (vaga de carro) mais próxima de (x,z), IGNORANDO ocupação. Usado
 *  quando o prédio está sem vaga de carro livre (todas reservadas por carros a caminho): o
 *  carro encosta no meio-fio mais próximo do prédio (não num cruzamento distante) e vira
 *  pessoa ALI. Devolve {x,z,heading} ou null. */
export function nearestCarCurb(i, x, z) {
  const L = CAR_SLOTS[i];
  if (!L || !L.length) return null;
  let best = L[0], bd = Infinity;
  for (const s of L) {
    const d = (s.x - x) ** 2 + (s.z - z) ** 2;
    if (d < bd) { bd = d; best = s; }
  }
  return { x: best.x, z: best.z, heading: best.heading };
}
export function releaseCarSlot(i, index) {
  if (index != null) CAR_OCC[i]?.delete(index);
}
/* ================== POSIÇÕES DE PESSOA — COROA CONTÍNUA ==================
 *
 * ANTES: uma LISTA FINITA de vagas (24 num lote 1×1, 46 no shopping). Quando
 * acabava, TODO MUNDO era empilhado numa COORDENADA ÚNICA — 200 pessoas no mesmo
 * prédio viravam uma pilha (medido: 66% a menos de 0,05u de outra), não uma multidão.
 *
 * AGORA: a quadra inteira ao redor do prédio — a COROA entre a borda da quadra e a
 * fachada — é um campo CONTÍNUO. A oferta é ILIMITADA (acquirePersonSlot nunca mais
 * devolve null) e o espaçamento ENCOLHE SOZINHO conforme a multidão cresce, porque a
 * área é fixa:  s(N) ≈ √(A / N).  É isso que dá "espaçamento médio parecido entre
 * todos", com a sobreposição aparecendo naturalmente no pico em vez de focos densos.
 *
 * CAMADA 0: enquanto a multidão for menor que as vagas de sempre (24 / 46), as
 * posições são EXATAMENTE as de hoje — meio da calçada, frente → laterais → fundos.
 * O caso comum (poucas pessoas) não muda em nada.
 *
 * `index` deixa de ser posição numa lista e vira um HANDLE OPACO: < n0 é camada 0,
 * ≥ n0 é campo. Ninguém fora daqui interpreta o valor (só o devolve no release).
 *
 * SUPERQUADRA: o campo não é a coroa de UM prédio, e sim o PISO INTEIRO do distrito —
 * as calçadas das 4 quadras, as passarelas e o largo. A coroa só faz sentido onde a
 * quadra é cercada por ruas; num distrito ela prenderia o avatar num anel de 36u
 * dentro de uma superquadra de 82u, com todo o parque em volta vazio. Continua
 * legível de quem é a multidão porque a camada 0 (as primeiras dezenas) fica na
 * calçada em volta do prédio e o termo de localidade puxa o campo para lá — a
 * multidão NASCE no prédio e transborda para o distrito conforme cresce.
 */

/** Profundidade `u`, medida da BORDA DA QUADRA para dentro: 0..SIDEWALK é calçada,
 *  SIDEWALK..SIDEWALK+PARK é o jardim, SIDEWALK+PARK é a fachada.
 *    • uMIN — a carroceria do carro estacionado chega a ~0,2 DENTRO da quadra; 1,0
 *      mantém a pessoa fora do carro.
 *    • uMAX — 0,4 de folga da fachada (nunca dentro da pegada). */
const SPOT_UMIN = 1.0;
const SPOT_UMAX = SIDEWALK + PARK - 0.4;
const SPOT_CANDIDATES = 16;   // candidatos por chegada (best-candidate de Mitchell)
const SPOT_LOCAL_W = 0.4;     // peso do "fique perto de onde cheguei" (desempate, não preferência)
const SPOT_LOCAL_REF = 40;    // distância (u) em que o termo de localidade satura
// Sequência R2 (número plástico) — baixa discrepância, DETERMINÍSTICA: o mesmo
// histórico de chegadas dá as mesmas posições, sem depender de Math.random.
const R2_A1 = 0.7548776662466927;
const R2_A2 = 0.5698402909980532;
const frac = (v) => v - Math.floor(v);

/** Geometria da coroa de cada prédio. A quadra (`land`) e a pegada são concêntricas,
 *  então a isolinha à profundidade `u` é o retângulo (W−2u)×(D−2u) centrado no prédio. */
const SPOT_GEOM = BUILDINGS.map((b) => {
  const W = b.land.right - b.land.left;
  const D = b.land.bottom - b.land.top;
  const Q = 2 * (W + D);
  // área da coroa entre uMIN e uMAX (integral de −P(u), P(u) = Q − 8u)
  const A = Q * (SPOT_UMAX - SPOT_UMIN) - 4 * (SPOT_UMAX ** 2 - SPOT_UMIN ** 2);
  return { W, D, Q, A, cx: b.x, cz: b.z };
});

/* --- campo da SUPERQUADRA: o piso do distrito inteiro --- */
const DIST_AREA = new Map(); // malha do distrito → área de piso (u²)
/** Área de piso do distrito, por amostragem em grade de 1u. Vale só para o
 *  espaçamento-alvo s(N)=√(A/N), então aproximar em 1u é de sobra — e é bem mais
 *  seguro do que somar/subtrair à mão retângulos arredondados, círculos e espigões. */
function pavedArea(W) {
  if (DIST_AREA.has(W)) return DIST_AREA.get(W);
  const L = W.d.land;
  let hits = 0, total = 0;
  for (let x = L.left + 0.5; x < L.right; x += 1) {
    for (let z = L.top + 0.5; z < L.bottom; z += 1) {
      total++;
      if (districtPavement(x, z) === W) hits++;
    }
  }
  const A = (hits / Math.max(1, total)) * L.w * L.d;
  DIST_AREA.set(W, A);
  return A;
}
/** Geometria do campo de um prédio de superquadra (`null` fora de distrito). */
const DIST_GEOM = BUILDINGS.map((b) => {
  const W = b.bed && districtWalk(b.bed.district);
  return W ? { W, land: W.d.land, A: pavedArea(W), cx: b.x, cz: b.z } : null;
});

/**
 * (ξ,η) ∈ [0,1)² → ponto da coroa, com JACOBIANO CONSTANTE — amostrar uniforme no
 * quadrado é amostrar uniforme em ÁREA. Sem a inversa da área, as faixas de fora
 * (perímetro maior) receberiam gente demais e o jardim ficaria vazio.
 * `η` percorre a isolinha a partir do MEIO DA FRENTE (+Z) no sentido +X.
 */
function spotAt(g, xi, eta) {
  const { W, D, Q, A, cx, cz } = g;
  const disc = Q * Q - 16 * (Q * SPOT_UMIN - 4 * SPOT_UMIN * SPOT_UMIN + xi * A);
  const u = (Q - Math.sqrt(Math.max(0, disc))) / 8;
  const a = W / 2 - u, c = D / 2 - u;
  const s = eta * 4 * (a + c);
  let x, z;
  if (s < a) { x = cx + s; z = cz + c; }                                        // meia-frente (+X)
  else if (s < a + 2 * c) { x = cx + a; z = cz + c - (s - a); }                 // lateral +X
  else if (s < 3 * a + 2 * c) { x = cx + a - (s - a - 2 * c); z = cz - c; }     // fundos
  else if (s < 3 * a + 4 * c) { x = cx - a; z = cz - c + (s - 3 * a - 2 * c); } // lateral −X
  else { x = cx - a + (s - 3 * a - 4 * c); z = cz + c; }                        // meia-frente (−X)
  return { x, z, heading: Math.atan2(cx - x, cz - z) }; // encara o prédio (igual às vagas de hoje)
}

/**
 * JANELA DA MULTIDÃO: o pedaço do distrito que a multidão de UM prédio ocupa. Ela
 * CRESCE com a multidão — começa no quarto de distrito do próprio prédio (a calçada em
 * volta dele, já incluindo as passarelas que ele divide com os vizinhos) e vai abrindo
 * até o distrito inteiro. É o que concilia as duas coisas: o avatar circula pelas 4
 * quadras, mas 10 pessoas não saem espalhadas pelos 82×82 — a multidão só transborda
 * quando de fato não cabe. Sem isso perde-se a leitura de QUAL prédio é a multidão,
 * que é a informação que o jogo existe para mostrar.
 */
const CROWD_SEED = 21;  // meia-janela inicial ≈ o quarto de distrito do prédio
const CROWD_AREA = 16;  // u² de piso por pessoa antes de precisar abrir
function crowdWindow(g, n) {
  const L = g.land;
  const pavedFrac = g.A / (L.w * L.d);
  const h = Math.max(CROWD_SEED, Math.sqrt((n * CROWD_AREA) / pavedFrac) / 2);
  return {
    left: Math.max(L.left, g.cx - h), right: Math.min(L.right, g.cx + h),
    top: Math.max(L.top, g.cz - h), bottom: Math.min(L.bottom, g.cz + h)
  };
}

/**
 * Ponto do PISO do distrito dentro da janela, por AMOSTRAGEM COM REJEIÇÃO: a região é
 * a diferença de retângulos arredondados, círculos e cápsulas, então não tem inversa
 * fechada como a coroa. O piso é ~40% do retângulo → ~2,5 sorteios por acerto, e a
 * sequência R2 avança a cada tentativa (nunca repete o mesmo ponto). Devolve `null` se
 * não achou piso — o chamador segue para o próximo candidato. `draw()` avança o
 * contador determinístico compartilhado.
 */
function districtSpotAt(g, draw, win) {
  const w = win.right - win.left, d = win.bottom - win.top;
  for (let t = 0; t < 12; t++) {
    const dw = draw();
    const x = win.left + frac(0.5 + R2_A1 * dw) * w;
    const z = win.top + frac(0.5 + R2_A2 * dw) * d;
    if (districtPavement(x, z) === g.W) {
      return { x, z, heading: Math.atan2(g.cx - x, g.cz - z) }; // encara o próprio prédio
    }
  }
  return null;
}

/* --- máscara de obstáculos (árvores e bancos) ---
 * Por que REJEITAR e não só penalizar: uma pessoa PARADA dentro do raio de contato de
 * um arbusto o mantém na lista de "ativos" do Trees.js — que reenvia a matriz de
 * TODAS as instâncias enquanto durar. Numa maratona de 8h isso é custo de frame
 * permanente, não um detalhe estético. */
const TREE_CANOPY_R = 1.35;  // = CANOPY_R do Trees.js
const TREE_PERSON_R = 1.0;   // = PERSON_R do Trees.js (radius = scale*CANOPY_R + PERSON_R)
const BENCH_R = 2.2;         // assento 3,4 × 1,1 + folga
let _obstacles = null;
function obstaclesFor(i) {
  if (!_obstacles) {
    // MESMA chamada (e MESMA seed) do Trees.js → a máscara enxerga exatamente as
    // árvores que vão ser desenhadas.
    const trees = generateTrees(WORLD.width, WORLD.depth, makeRandom(99001));
    _obstacles = BUILDINGS.map((b) => {
      // superquadra: o campo cobre as 4 quadras, então a máscara também — senão a
      // pessoa que transborda para o lado do vizinho pousaria dentro dos arbustos dele
      const L = b.bed ? b.bed.district.land : b.land, out = [];
      const inLand = (x, z) => x > L.left - 2 && x < L.right + 2 && z > L.top - 2 && z < L.bottom + 2;
      for (const t of trees) if (inLand(t.x, t.z)) out.push({ x: t.x, z: t.z, r: t.scale * TREE_CANOPY_R + TREE_PERSON_R });
      for (const bn of BENCHES) if (!bn.noDraw && inLand(bn.x, bn.z)) out.push({ x: bn.x, z: bn.z, r: BENCH_R });
      return out;
    });
  }
  return _obstacles[i];
}
function blocked(obs, x, z) {
  for (const o of obs) if ((o.x - x) ** 2 + (o.z - z) ** 2 < o.r * o.r) return true;
  return false;
}

/* --- estado por prédio --- */
const PED0 = BUILDINGS.map(pedSlotsFor);              // camada 0 = as vagas de HOJE
/** Quantas posições da camada 0 (as vagas de sempre) cada prédio tem. */
export const PERSON_LAYER0_COUNT = PED0.map((l) => l.length);
const PED = BUILDINGS.map(() => ({
  l0: new Set(),      // índices ocupados da camada 0
  live: new Map(),    // handle → {x,z} de TODOS os ocupados (camada 0 + campo)
  next: 0,            // próximo handle do campo
  draw: 0             // contador da sequência R2
}));

/* VIZINHANÇA COMPARTILHADA: fora de distrito o campo de cada prédio é a coroa dele —
 * dois prédios nunca disputam o mesmo metro quadrado, e olhar só a própria lista basta.
 * Na SUPERQUADRA os 4 prédios dividem o MESMO piso: se cada um enxergasse apenas a
 * própria multidão, o best-candidate acharia "vazio" onde já tem gente do vizinho e as
 * quatro se sobreporiam. Este índice por distrito é a visão única do piso. */
const DIST_LIVE = new Map(); // malha do distrito → Map("i:handle" → {x,z})
function liveSetOf(i) {
  const dg = DIST_GEOM[i];
  if (!dg) return PED[i].live;
  if (!DIST_LIVE.has(dg.W)) DIST_LIVE.set(dg.W, new Map());
  return DIST_LIVE.get(dg.W);
}
function liveAdd(i, handle, x, z) {
  const p = { x, z };
  PED[i].live.set(handle, p);
  if (DIST_GEOM[i]) liveSetOf(i).set(`${i}:${handle}`, p);
}
function liveDel(i, handle) {
  PED[i].live.delete(handle);
  if (DIST_GEOM[i]) liveSetOf(i).delete(`${i}:${handle}`);
}

/** Quantas pessoas estão posicionadas no prédio `i` agora. */
export function personSpotCount(i) {
  return PED[i] ? PED[i].live.size : 0;
}

/** Posição do campo contínuo: sorteia SPOT_CANDIDATES candidatos de baixa discrepância
 *  e fica com o que estiver MAIS LONGE do vizinho mais próximo (best-candidate), com um
 *  desempate suave por proximidade de onde a pessoa chegou. O termo de localidade é
 *  normalizado pelo espaçamento-alvo → encolhe junto com a multidão e nunca vira
 *  preferência (senão uma face lotaria e as outras ficariam vazias). */
function acquireField(i, fromX, fromZ) {
  const dg = DIST_GEOM[i];                       // superquadra → piso do distrito
  const g = dg || SPOT_GEOM[i];
  const st = PED[i], obs = obstaclesFor(i);
  const live = liveSetOf(i); // no distrito: a multidão dos 4 prédios, num piso só
  const sAlvo = Math.sqrt(g.A / (live.size + 1));
  // No distrito o ímã é o PRÓPRIO PRÉDIO, não o ponto de chegada: com a janela aberta
  // é ele que segura a multidão junto do dono dela.
  const ax = dg ? g.cx : fromX;
  const az = dg ? g.cz : fromZ;
  const local = ax != null && az != null;
  const win = dg ? crowdWindow(g, st.live.size) : null;
  let best = null, bestScore = -Infinity;      // melhor entre os NÃO bloqueados
  let fallback = null, fallbackScore = -Infinity; // melhor ignorando a máscara
  for (let j = 0; j < SPOT_CANDIDATES; j++) {
    const next = () => ++st.draw;
    const d = next();
    const p = dg ? districtSpotAt(g, next, win) : spotAt(g, frac(0.5 + R2_A1 * d), frac(0.5 + R2_A2 * d));
    if (!p) continue; // não achou piso na janela em 12 sorteios → tenta outro candidato
    let dnn = Infinity;
    for (const q of live.values()) {
      const dd = (q.x - p.x) ** 2 + (q.z - p.z) ** 2;
      if (dd < dnn) dnn = dd;
    }
    let score = dnn === Infinity ? 1e9 : Math.sqrt(dnn);
    if (local) {
      const da = Math.hypot(p.x - ax, p.z - az);
      score -= SPOT_LOCAL_W * sAlvo * Math.min(da, SPOT_LOCAL_REF) / SPOT_LOCAL_REF;
    }
    if (score > fallbackScore) { fallbackScore = score; fallback = p; }
    if (blocked(obs, p.x, p.z)) continue;
    if (score > bestScore) { bestScore = score; best = p; }
  }
  // rede de segurança: nenhum sorteio caiu no piso (janela pequena e cheia de canteiro)
  // → sorteia no distrito inteiro; em último caso, a porta. Nunca devolve null.
  if (!best && !fallback && dg) {
    fallback = districtSpotAt(g, () => ++st.draw, g.land) ||
      { ...districtWalkGate(g.W, g.cx, g.cz), heading: 0 };
  }
  const p = best || fallback; // todos bloqueados (não deve ocorrer) → nunca trava
  const handle = PERSON_LAYER0_COUNT[i] + st.next++;
  liveAdd(i, handle, p.x, p.z);
  return { index: handle, x: p.x, z: p.z, heading: p.heading };
}

/**
 * Vagas da camada 0 que caem DENTRO de um obstáculo (arbusto/banco), por prédio.
 *
 * O `acquireField` já rejeitava posições assim, e o comentário lá explica o porquê: uma
 * pessoa parada dentro do raio de contato de um arbusto o mantém "ativo" no Trees, que
 * então reenvia a matriz da vegetação inteira enquanto durar. Só que a camada 0 — onde
 * param as primeiras dezenas de pessoas de CADA prédio, ou seja, o caso comum — não
 * passava por essa máscara. MEDIDO na geometria real: 17 de 334 vagas de camada 0 (5,1%),
 * espalhadas por 12 dos 13 prédios; bastavam 5 pessoas num prédio para prender 2 arbustos.
 *
 * A vaga não é REMOVIDA da lista: os índices da camada 0 são os próprios handles e
 * `PERSON_LAYER0_COUNT` desloca os handles do campo. Ela só deixa de ser entregue.
 *
 * Lazy porque os obstáculos dependem das árvores, registradas depois deste módulo.
 */
const _pedBloqueada = [];
function pedBloqueada(i) {
  if (!_pedBloqueada[i]) {
    const obs = obstaclesFor(i), L = PED0[i];
    const m = new Uint8Array(L.length);
    for (let s = 0; s < L.length; s++) if (blocked(obs, L[s].x, L[s].z)) m[s] = 1;
    _pedBloqueada[i] = m;
  }
  return _pedBloqueada[i];
}

/** Posição para uma pessoa no prédio `i`. NUNCA devolve null. Camada 0 (as vagas de
 *  sempre, frente → laterais → fundos) enquanto couber; depois, campo contínuo. */
export function acquirePersonSlot(i) {
  const st = PED[i], L = PED0[i], bloq = pedBloqueada(i);
  for (let s = 0; s < L.length; s++) {
    if (bloq[s]) continue; // dentro de um arbusto/banco — ver pedBloqueada
    if (!st.l0.has(s)) {
      st.l0.add(s);
      liveAdd(i, s, L[s].x, L[s].z);
      return { index: s, x: L[s].x, z: L[s].z, heading: L[s].heading };
    }
  }
  return acquireField(i, null, null);
}

/** Idem, mas sabendo de onde a pessoa está chegando: na camada 0 pega a vaga LIVRE
 *  mais próxima (caminhada curta, mesma face); no campo, o mesmo desempate suave. */
export function acquirePersonSlotNear(i, x, z) {
  const st = PED[i], L = PED0[i], bloq = pedBloqueada(i);
  let best = -1, bd = Infinity;
  for (let s = 0; s < L.length; s++) {
    if (bloq[s] || st.l0.has(s)) continue; // ver pedBloqueada
    const d = (L[s].x - x) ** 2 + (L[s].z - z) ** 2;
    if (d < bd) { bd = d; best = s; }
  }
  if (best === -1) return acquireField(i, x, z);
  st.l0.add(best);
  liveAdd(i, best, L[best].x, L[best].z);
  return { index: best, x: L[best].x, z: L[best].z, heading: L[best].heading };
}

export function releasePersonSlot(i, index) {
  if (index == null) return;
  const st = PED[i];
  if (!st) return;
  liveDel(i, index);
  if (index < PERSON_LAYER0_COUNT[i]) st.l0.delete(index);
}

/** Reaper anti-vazamento (para o servidor/telemetria): descarta todo handle que não
 *  esteja em `liveIndexes`. Devolve quantos foram recuperados. */
export function personSpotReconcile(i, liveIndexes) {
  const st = PED[i];
  if (!st) return 0;
  const keep = liveIndexes instanceof Set ? liveIndexes : new Set(liveIndexes || []);
  let n = 0;
  for (const h of [...st.live.keys()]) if (!keep.has(h)) { releasePersonSlot(i, h); n++; }
  return n;
}

/**
 * PORTÃO: projeção RADIAL de (x,z) na linha de caminhada da calçada (u = SIDEWALK/2)
 * da face MAIS PRÓXIMA. É a última escala de qualquer rota até/desde uma posição da
 * coroa: o segmento portão↔posição tem x OU z constante, então nunca corta a pegada
 * do prédio (era o que o antigo filtro por distância evitava na marra, ao custo de
 * jogar todo mundo num ponto só).
 *
 * SUPERQUADRA: a "face mais próxima da quadra" não quer dizer nada — o piso é o do
 * distrito inteiro. Ali o portão é o ponto mais próximo da MALHA DE CAMINHADA
 * (cityLayout.districtWalkGate), que cumpre o mesmo contrato: perna curta, sempre
 * sobre o piso, e emendando exatamente numa aresta do SidewalkGraph.
 */
export function personSpotGate(i, x, z) {
  const dg = DIST_GEOM[i];
  if (dg) return districtWalkGate(dg.W, x, z);
  const L = BUILDINGS[i].land, W = SIDEWALK / 2;
  const dl = x - L.left, dr = L.right - x, dt = z - L.top, db = L.bottom - z;
  const m = Math.min(dl, dr, dt, db);
  if (m === db) return { x, z: L.bottom - W };   // frente (+Z)
  if (m === dt) return { x, z: L.top + W };      // fundos (−Z)
  if (m === dl) return { x: L.left + W, z };     // lateral −X
  return { x: L.right - W, z };                  // lateral +X
}

/**
 * SAÍDA: de uma posição da coroa até um ponto GARANTIDAMENTE na rua, sem cortar a
 * pegada. O passo radial fixo que existia antes (`_awayFromBuilding(a, 4.8)`) só
 * funcionava para o único anel de vagas de então — medido, ele já falhava em 3,6% das
 * posições de HOJE (as pontas das faces longas do shopping) e falharia em 58% das
 * posições da coroa, fazendo o carro nascer em cima da calçada.
 *   portão → (portão da face do meio-fio) → vaga de carro (sempre na rua)
 */
export function personSpotExit(i, x, z) {
  const gate = personSpotGate(i, x, z);
  const curb = nearestCarCurb(i, gate.x, gate.z) || { x: gate.x, z: gate.z };
  const gate2 = personSpotGate(i, curb.x, curb.z);
  return { gate, gate2, curb };
}

/**
 * SAÍDA PELO PORTAL — modo SEM COLISÃO entre carros (o único em que ela é usada).
 *
 * O QUE ESTAVA ERRADO. `personSpotExit` termina em `nearestCarCurb`, que devolve a VAGA DE
 * CARRO mais próxima — e vaga é recurso do estacionamento, não geometria da face. Com o
 * prédio movimentado, a vaga livre mais perto costuma estar em OUTRA FACE: a pessoa saía da
 * posição dela, ia até o portão, e então contornava a quadra pela calçada até a esquina
 * para só ali virar carro. No telão isso aparece como gente andando um quarteirão inteiro
 * de lado antes de "virar carro" num canto.
 *
 * O QUE ESTA FUNÇÃO FAZ. Sem colisão entre carros não existe reserva de vaga: qualquer
 * ponto do meio-fio serve. Então a saída é puramente geométrica — a FACE em que a pessoa
 * já está. Usa os mesmos `carPortals` da chegada (as faces que de fato têm rua, com a
 * margem de esquina já embutida em `lo`/`hi`), projeta a pessoa dentro do segmento e
 * devolve os dois pontos da perna curta:
 *
 *   pessoa → gate (linha de caminhada da calçada, MESMA face) → curb (meio-fio, na rua)
 *
 * O `gate` sai da geometria do próprio portal — normal para a rua, recuada de CAR_CURB
 * (meio-fio) mais meia calçada —, então bate exatamente com o que `personSpotGate` daria
 * para aquela face, sem depender de qual face é "a mais próxima".
 *
 * O DESVIO É PEQUENO E DETERMINÍSTICO (derivado do id): serve só para desempatar duas
 * pessoas que saem do mesmo ponto da coroa, não para espalhar — quem espalha é a própria
 * posição de cada uma, que já é distinta.
 *
 * @returns {{gate:{x,z}, curb:{x,z}, heading:number, fx:number, fz:number, face:string}|null}
 *          `null` quando o prédio não tem nenhuma face com rua → o chamador mantém o fluxo
 *          antigo (é o mesmo contrato que a chegada por portal já usa).
 */
// Desvio de desempate. Pequeno de propósito: quem espalha é a posição de cada pessoa na
// coroa, que já é distinta. Ele só existe para duas pessoas que saem do MESMO ponto não
// mirarem o MESMO ponto de meio-fio. Medido: com 3u o pior caso de caminhada nas cidades
// do funil subia de 9,0u para 11,1u (quem sai colado na esquina já paga a margem de 6u do
// portal e o desvio somava por cima); com 2u o custo cai pela metade e o espalhamento
// continua suficiente (124 pontos distintos para 150 pessoas no mesmo prédio).
const EXIT_JITTER = 2;
const prender = (v, lo, hi) => (v < lo ? lo : v > hi ? hi : v);

/** Hash estável de string → [0,1). FNV-1a: mesmo jogador, mesmo desvio, sempre. */
function hash01(s) {
  let h = 0x811c9dc5;
  const t = String(s == null ? '' : s);
  for (let i = 0; i < t.length; i++) { h ^= t.charCodeAt(i); h = Math.imul(h, 0x01000193); }
  return ((h >>> 0) % 100000) / 100000;
}

export function personPortalExit(i, x, z, playerId) {
  const portals = carPortals(i);
  if (!portals.length) return null;

  // FACE MAIS PRÓXIMA DA PESSOA — distância ao SEGMENTO do portal (não ao centro dele):
  // numa face longa o centro pode estar a dezenas de unidades de quem está na ponta.
  let melhor = null, melhorD = Infinity;
  for (const P of portals) {
    const along = P.axis === 'x' ? x : z;
    const t = prender(along, P.lo, P.hi);
    const px = P.axis === 'x' ? t : P.fixed;
    const pz = P.axis === 'x' ? P.fixed : t;
    const d = (px - x) ** 2 + (pz - z) ** 2;
    if (d < melhorD) { melhorD = d; melhor = { P, t }; }
  }
  const P = melhor.P;

  // DESVIO DETERMINÍSTICO dentro do segmento (a margem de esquina já está em lo/hi).
  //
  // Quem está DENTRO do segmento já vem espalhado pela própria posição na coroa, então
  // basta um desvio simétrico pequeno para desempatar quem sai do mesmo ponto.
  //
  // Quem está FORA — além da margem de esquina — é todo prendido no MESMO extremo, e aí
  // o desvio simétrico não espalha nada (metade é reprendida no mesmo lugar). Para esses o
  // desvio é PARA DENTRO, a única direção que existe ali, e um pouco maior: eles já pagam
  // a caminhada ao longo da face, e alguns metros a mais desmancham a pilha. MEDIDO com
  // 150 pessoas no mesmo prédio: 11 mirando o mesmo ponto de meio-fio → 3.
  const along0 = P.axis === 'x' ? x : z;
  const meia = Math.max(0, (P.hi - P.lo) / 2);
  const h = hash01(playerId);
  let along;
  if (melhor.t <= P.lo + 1e-6 && along0 < P.lo) along = P.lo + h * Math.min(EXIT_JITTER * 2, meia);
  else if (melhor.t >= P.hi - 1e-6 && along0 > P.hi) along = P.hi - h * Math.min(EXIT_JITTER * 2, meia);
  else along = prender(melhor.t + (h * 2 - 1) * Math.min(EXIT_JITTER, meia), P.lo, P.hi);

  const curb = P.axis === 'x' ? { x: along, z: P.fixed } : { x: P.fixed, z: along };
  // GATE: recua do meio-fio para a linha de caminhada da calçada, pela NORMAL da face.
  // `fixed` é a borda da quadra deslocada de CAR_CURB para dentro da rua, então voltar
  // (CAR_CURB + meia calçada) cai exatamente na linha de caminhada daquela face.
  const recuo = CAR_CURB + WALK;
  const gate = { x: curb.x - P.nx * recuo, z: curb.z - P.nz * recuo };

  return { gate, curb, heading: P.heading, fx: P.fx, fz: P.fz, face: P.face };
}

/**
 * O segmento (x0,z0)→(x1,z1) atravessa a "pegada" do edifício `i`? Usado para NÃO deixar
 * uma pessoa recém-desembarcada caminhar em LINHA RETA através do prédio até uma vaga da
 * face oposta (comum quando o prédio está lotado e a vaga livre mais próxima fica do outro
 * lado) — nesse caso o CityAgents roteia pela CALÇADA (contorna a quadra). `margin` infla
 * a pegada p/ tratar como "cruza" também quem passaria raspando o prédio.
 */
export function segCrossesBuilding(i, x0, z0, x1, z1, margin = 1.5) {
  const b = BUILDINGS[i];
  if (!b) return false;
  const minx = b.x - b.w / 2 - margin, maxx = b.x + b.w / 2 + margin;
  const minz = b.z - b.d / 2 - margin, maxz = b.z + b.d / 2 + margin;
  // Liang–Barsky: recorta o segmento [0,1] contra a AABB; sobra intervalo ⇒ cruza.
  let t0 = 0, t1 = 1;
  const dx = x1 - x0, dz = z1 - z0;
  const clip = (p, q) => {
    if (p === 0) return q >= 0;            // paralelo a esta face: dentro só se já na faixa
    const r = q / p;
    if (p < 0) { if (r > t1) return false; if (r > t0) t0 = r; }
    else { if (r < t0) return false; if (r < t1) t1 = r; }
    return true;
  };
  if (clip(-dx, x0 - minx) && clip(dx, maxx - x0) && clip(-dz, z0 - minz) && clip(dz, maxz - z0)) {
    return t0 <= t1;
  }
  return false;
}
